/**
 * HUB-F — Données réparations pour onglet Centre Activité.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  computeMagasinRepairCounts,
  isAttentePiece,
  isData01InProgress,
  isEnCoursSection,
  isInDateRange,
  isLivre,
  isPretLivrer,
  isTermine,
  matchesRepairSearch,
} from '../../utils/reparations/reparationsDisplay';

export default function useActiviteReparations(api, filters, { enabled = true } = {}) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!api || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200', include_delivered: 'true' });
      if (filters.magasin_id) params.append('magasin_id', filters.magasin_id);
      if (filters.search) params.append('search', filters.search);
      const response = await api.get(`/devices?${params.toString()}`);
      setDevices(response.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur chargement réparations');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [api, enabled, filters.magasin_id, filters.search]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filteredDevices = useMemo(() => (
    devices.filter((d) => (
      isInDateRange(d.created_at || d.updated_at, filters.date_debut, filters.date_fin)
      && matchesRepairSearch(d, filters.search)
    ))
  ), [devices, filters]);

  const recent = useMemo(
    () => [...filteredDevices].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)).slice(0, 20),
    [filteredDevices],
  );

  const enCours = useMemo(
    () => filteredDevices.filter((d) => isEnCoursSection(d.status)),
    [filteredDevices],
  );

  const attentePiece = useMemo(
    () => filteredDevices.filter((d) => isAttentePiece(d.status)),
    [filteredDevices],
  );

  const termine = useMemo(
    () => filteredDevices.filter((d) => isTermine(d.status) || isPretLivrer(d.status)),
    [filteredDevices],
  );

  const magasinCounts = useMemo(
    () => computeMagasinRepairCounts(filteredDevices),
    [filteredDevices],
  );

  const kpis = useMemo(() => ({
    total: filteredDevices.length,
    en_cours: filteredDevices.filter((d) => isData01InProgress(d.status)).length,
    en_cours_strict: enCours.length,
    attente_piece: attentePiece.length,
    termine: termine.length,
    livre: filteredDevices.filter((d) => isLivre(d.status)).length,
    recent: recent.length,
  }), [filteredDevices, enCours, attentePiece, termine, recent]);

  return {
    devices: filteredDevices,
    recent,
    enCours,
    attentePiece,
    termine,
    magasinCounts,
    kpis,
    loading,
    error,
    refresh: fetchAll,
  };
}
